/**
 * InflictionColumn — deque stacking, cross-element reaction trigger,
 * co-active duration extension, arts burst flag.
 *
 * Config-driven: stack limit and isArts come from the status JSON config.
 * Cross-element reaction mapping still uses INFLICTION_TO_REACTION.
 */

import { NounType } from '../../../dsl/semantics';
import { EventStatusType } from '../../../consts/enums';
import { eventDuration, setEventDuration } from '../../../consts/viewTypes';
import { ENEMY_ID, INFLICTION_TO_REACTION } from '../../../model/channels';
import { getStatusById } from '../../gameDataStore';
import { allocDerivedEvent } from '../objectPool';
import { genEventUid } from '../inputEventController';
import type { EventColumn, ColumnHost, EventSource, AddOptions, ConsumeOptions } from './eventColumn';

export class InflictionColumn implements EventColumn {
  readonly columnId: string;
  private host: ColumnHost;
  private isArts: boolean;
  private maxStacks: number;

  constructor(columnId: string, host: ColumnHost) {
    this.columnId = columnId;
    this.host = host;
    const config = getStatusById(columnId);
    this.isArts = config?.eventCategoryType === NounType.INFLICTION;
    this.maxStacks = (config?.stacks?.limit as { value?: number } | undefined)?.value ?? 4;
  }

  add(ownerEntityId: string, frame: number, durationFrames: number,
    source: EventSource, options?: AddOptions): boolean {

    // ── Cross-element reaction (arts inflictions only) ──────────────────
    if (this.isArts) {
      const otherActive = this.collectCrossElementActive(ownerEntityId, frame);
      if (otherActive.length > 0) {
        const reactionColumnId = INFLICTION_TO_REACTION[this.columnId];
        if (reactionColumnId) {
          for (const consumedOther of otherActive) {
            setEventDuration(consumedOther, frame - consumedOther.startFrame);
            consumedOther.eventStatus = EventStatusType.CONSUMED;
            if (source.sourceEventUid) this.host.linkTransition(consumedOther.uid, source.sourceEventUid);
          }
          // Precompute the incoming infliction's uid so we can both pass it
          // as the primary causal parent of the reaction AND use it as the
          // uid of the zero-dur consumed copy emitted below.
          const incomingUid = options?.uid ?? `${this.columnId}-${genEventUid()}`;
          // Read reaction duration from JSON config via the resolved durationSeconds getter
          const reactionConfig = getStatusById(reactionColumnId);
          const reactionDurSec = reactionConfig?.durationSeconds ?? 20;
          const reactionDurFrames = Math.round(reactionDurSec * 120);
          // Causal parents for the reaction: [incoming, ...activeOther].
          // Incoming is primary (most recent triggering event); the consumed
          // cross-element inflictions are additional sources.
          const reactionParents = [incomingUid, ...otherActive.map(o => o.uid)];
          this.host.applyToColumn(reactionColumnId, ENEMY_ID, frame, reactionDurFrames, source, {
            uid: `${incomingUid}-reaction`,
            statusLevel: Math.min(otherActive.length, 4) as import('../../../consts/types').StatusLevel,
            parents: reactionParents,
            ...(options?.event ? { event: options.event } : {}),
          });
          // Emit a consumed copy of the incoming infliction for freeform state tracking
          const consumed = allocDerivedEvent();
          consumed.uid = incomingUid;
          consumed.id = this.columnId;
          consumed.name = this.columnId;
          consumed.ownerEntityId = ownerEntityId;
          consumed.columnId = this.columnId;
          consumed.startFrame = frame;
          consumed.segments = [{ properties: { duration: 0 } }];
          consumed.sourceEntityId = source.ownerEntityId;
          consumed.sourceSkillId = source.skillName;
          consumed.eventStatus = EventStatusType.CONSUMED;
          if (options?.event) Object.assign(consumed, options.event);
          if (source.sourceEventUid) this.host.linkTransition(consumed.uid, source.sourceEventUid);
          this.host.pushToOutput(consumed);
          return true;
        }
      }
    }

    // ── Deque stacking: evict oldest at cap ──────────────────────────────
    const active = this.host.activeEventsIn(this.columnId, ownerEntityId, frame);
    const isArtsBurst = this.isArts && active.length > 0;

    if (active.length >= this.maxStacks) {
      const oldest = active[0];
      setEventDuration(oldest, frame - oldest.startFrame);
      oldest.eventStatus = EventStatusType.CONSUMED;
      if (source.sourceEventUid) this.host.linkTransition(oldest.uid, source.sourceEventUid);
    }

    // ── Create event ─────────────────────────────────────────────────────
    const ev = allocDerivedEvent();
    ev.uid = options?.uid ?? `${this.columnId}-${genEventUid()}`;
    ev.id = this.columnId;
    ev.name = this.columnId;
    ev.ownerEntityId = ownerEntityId;
    ev.columnId = this.columnId;
    ev.startFrame = frame;
    ev.segments = [{ properties: { duration: durationFrames } }];
    ev.sourceEntityId = source.ownerEntityId;
    ev.sourceSkillId = source.skillName;
    if (isArtsBurst) ev.isArtsBurst = true;
    if (options?.event) Object.assign(ev, options.event);

    this.host.pushEvent(ev);

    // Record stack position at creation time
    const activeAfterCreation = this.host.activeEventsIn(this.columnId, ownerEntityId, frame);
    ev.stacks = activeAfterCreation.length;

    // ── Extend co-active inflictions to match new end ────────────────────
    const newEnd = ev.startFrame + eventDuration(ev);
    for (const act of activeAfterCreation) {
      if (act.uid === ev.uid) continue;
      const actEnd = act.startFrame + eventDuration(act);
      if (newEnd > actEnd) {
        setEventDuration(act, newEnd - act.startFrame);
        act.eventStatus = EventStatusType.EXTENDED;
        if (source.sourceEventUid) this.host.linkTransition(act.uid, source.sourceEventUid);
        // Move the queued EVENT_END hook so IS_NOT fires at the new end,
        // not the old (shorter) end. Without this, BECOME-NOT talents
        // (e.g. Freezing Point) consume early when the first infliction's
        // original end hook fires even though the state is still active.
        this.host.rescheduleEventEnd(act.uid, newEnd);
      }
    }

    return true;
  }

  consume(ownerEntityId: string, frame: number, source: EventSource,
    options?: ConsumeOptions): number {
    const count = options?.count ?? Infinity;
    const allActive = this.host.activeEventsIn(this.columnId, ownerEntityId, frame)
      .sort((a, b) => a.startFrame - b.startFrame);
    const toAbsorb = allActive.slice(0, count);
    for (const ev of toAbsorb) {
      setEventDuration(ev, frame - ev.startFrame);
      ev.eventStatus = EventStatusType.CONSUMED;
      if (source.sourceEventUid) this.host.linkTransition(ev.uid, source.sourceEventUid);
    }
    return toAbsorb.length;
  }

  canAdd(): boolean { return true; } // deque always accepts (evicts oldest)

  canConsume(ownerEntityId: string, frame: number): boolean {
    return this.host.activeCount(this.columnId, ownerEntityId, frame) > 0;
  }

  // ── Private ────────────────────────────────────────────────────────────

  private collectCrossElementActive(ownerEntityId: string, frame: number) {
    const result: import('../../../consts/viewTypes').TimelineEvent[] = [];
    // Check all other infliction columns that map to reactions
    for (const otherCol of Object.keys(INFLICTION_TO_REACTION)) {
      if (otherCol === this.columnId) continue;
      for (const ev of this.host.activeEventsIn(otherCol, ownerEntityId, frame)) {
        result.push(ev);
      }
    }
    return result;
  }
}

